/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { buildCacheKeyWithUrlTokens, removeDomainQspFromUrl } from '@msdyn365-commerce-modules/retail-actions';

import { IDobbiesDefaultGeneralPageSummaryConfig } from './dobbies-default-general-page-summary.props.autogenerated';

export interface IPageSummaryData {
    title?: string;
    description?: string;
    keywords?: string;
    sharingImageUrl?: string;
    canonicalUrl?: string;
    faviconUrl?: string;
}

/**
 * Page Summary Input
 */
export class PageSummaryInput implements IActionInput {
    public requestContext: IRequestContext;

    public config: IDobbiesDefaultGeneralPageSummaryConfig;

    constructor(config: IDobbiesDefaultGeneralPageSummaryConfig, requestContext: IRequestContext) {
        this.config = config || {};
        this.requestContext = requestContext;
    }

    public getCacheObjectType = (): string => 'PageSummary';

    public getCacheKey = (): string => `${buildCacheKeyWithUrlTokens('PageSummary', this.requestContext)}-${this.config.title}`;

    public dataCacheType = (): CacheType => 'request';
}

const action = async (input: PageSummaryInput, context: IActionContext): Promise<IPageSummaryData> => {
    const { config } = input;
    try {
        const url = context.requestContext.url.requestUrl.href;
        const excelUrl = context.requestContext.app.config.defaultPageSeoTSVUrl;
        if (!excelUrl) {
            return {
                title: config && config.title,
                description: config && config.description,
                keywords: config && config.keywords,
                sharingImageUrl: config && config.sharingImage && config.sharingImage.src,
                canonicalUrl:
                    context.requestContext && context.requestContext.canonicalUrl
                        ? removeDomainQspFromUrl(context.requestContext.canonicalUrl, context.requestContext)
                        : undefined,
                faviconUrl: config && config.faviconUrl
            };
        }
        const excelRes = await Msdyn365.sendRequest<any>(excelUrl, 'get');
        const excelDataStr = excelRes.data as string;
        const splittedExcelData = excelDataStr.split('\n');
        let title = '';
        let desc: string | undefined;
        let canonical = '';

        for (const dataRow of splittedExcelData) {
            if (!dataRow || !dataRow.trim()) {
                continue;
            }
            const rowSplitted = dataRow.trim().split('\t');
            if (rowSplitted && rowSplitted.length > 3) {
                if (rowSplitted[0].includes(url) || rowSplitted[0] === url) {
                    title = rowSplitted[1];
                    desc = rowSplitted[2];
                    canonical = rowSplitted[3];
                    return {
                        title: title,
                        description: desc,
                        keywords: config && config.keywords,
                        sharingImageUrl: config && config.sharingImage && config.sharingImage.src,
                        canonicalUrl: canonical,
                        faviconUrl: config && config.faviconUrl
                    };
                }
            }
        }
    } catch (e) {
        console.log('Error processing Metadata TSV file', e);
    }

    return {
        title: config && config.title,
        description: config && config.description,
        keywords: config && config.keywords,
        sharingImageUrl: config && config.sharingImage && config.sharingImage.src,
        canonicalUrl:
            context.requestContext && context.requestContext.canonicalUrl
                ? removeDomainQspFromUrl(context.requestContext.canonicalUrl, context.requestContext)
                : undefined,
        faviconUrl: config && config.faviconUrl
    };
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/default-page-summary',
    action: <IAction<IPageSummaryData>>action,
    input: (args: ICreateActionContext) => {
        return new PageSummaryInput(<IDobbiesDefaultGeneralPageSummaryConfig>args.config, args.requestContext);
    }
});
